import { FormEvent, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';
import axios from 'axios';

type AuthMode = 'login' | 'signup';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: Location })?.from?.pathname ?? '/app/reports';
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const toggleMode = () => {
    setMode((prev) => (prev === 'login' ? 'signup' : 'login'));
    setError(null);
    setNotice(null);
    setPassword('');
    setConfirmPassword('');
  };

  const extractErrorMessage = useMemo(
    () => (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.data) {
        const data = error.response.data as Record<string, unknown>;
        if (typeof data.detail === 'string') return data.detail;
        for (const value of Object.values(data)) {
          if (typeof value === 'string') return value;
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') return value[0];
        }
      }
      return null;
    },
    []
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);

    const normalizedEmail = email.trim().toLowerCase();

    if (mode === 'signup') {
      if (!normalizedEmail) {
        setError('Email is required to create an account.');
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords must match.');
        setLoading(false);
        return;
      }
      try {
        await authApi.registerUser({
          username: normalizedEmail,
          email: normalizedEmail,
          password
        });
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        setNotice('Access requested. A Gridfield administrator must verify your account before you can sign in.');
        return;
      } catch (err) {
        const message = extractErrorMessage(err) ?? 'Unable to create your account. Please try again or contact support.';
        setError(message);
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      await login(normalizedEmail || email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const message = extractErrorMessage(err) ?? 'Sign in failed. Check your credentials or try resetting your password.';
      setError(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isSignup = mode === 'signup';

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#f5f5f5] px-4 py-16 text-[#0f0f19]">
      <div className="absolute inset-0 hero-spotlight opacity-60" aria-hidden="true" />
      <div className="relative z-10 w-full max-w-lg rounded-[32px] border border-black/10 bg-white p-10 shadow-[0_35px_80px_rgba(0,0,0,0.08)]">
        <div className="mb-8 text-center">
          <Link
            to="/"
            className="mb-4 inline-flex items-center justify-center gap-3 text-left text-xs uppercase tracking-[0.4em] text-[#5a5b6c]"
          >
            <img src="/brand/logo-symbol-black.png" alt="gridfield" className="h-6 w-auto" />
            <span>Workspace</span>
          </Link>
        </div>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-[#3a3b4c]">
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0f0f19] placeholder-[#9a9bac] focus:border-black/40 focus:outline-none focus:ring-2 focus:ring-black/20"
              autoComplete="email"
              required
            />
          </label>
          <label className="block text-sm font-medium text-[#3a3b4c]">
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0f0f19] placeholder-[#9a9bac] focus:border-black/40 focus:outline-none focus:ring-2 focus:ring-black/20"
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              required
            />
          </label>
          {isSignup && (
            <label className="block text-sm font-medium text-[#3a3b4c]">
              Confirm password
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0f0f19] placeholder-[#9a9bac] focus:border-black/40 focus:outline-none focus:ring-2 focus:ring-black/20"
                autoComplete="new-password"
                required
              />
            </label>
          )}
          {error && (
            <p className="rounded-2xl border border-red-500/30 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>
          )}
          {notice && (
            <p className="rounded-2xl border border-emerald-500/30 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
              {notice}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#0f0f19] px-6 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? (isSignup ? 'Requesting access…' : 'Signing in…') : isSignup ? 'Request access' : 'Sign in'}
          </button>
        </form>
        <p className="mt-8 text-center text-xs text-[#5a5b6c]">
          {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            type="button"
            onClick={toggleMode}
            className="font-semibold text-[#0f0f19] underline-offset-4 hover:underline"
          >
            {isSignup ? 'Sign in instead' : 'Request access'}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
